import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DollarSign, PlayCircle, Check, Loader2, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import client, { apiErrorMessage } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { TableSkeleton } from '@/components/Skeletons';
import Pagination from '@/components/PaginationBar';
import EmptyState from '@/components/EmptyState';
import StatusBadge from '@/components/StatusBadge';
import { formatCurrency, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function PlatformBilling() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const now = new Date();
  const [genOpen, setGenOpen] = useState(false);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const costQ = useQuery({
    queryKey: ['cost-per-customer'],
    queryFn: () => client.get('/platform-billing/cost-per-customer').then((r) => r.data.data),
  });
  const [costEdit, setCostEdit] = useState(false);
  const [costValue, setCostValue] = useState('');

  const q = useQuery({
    queryKey: ['platform-invoices', { page, statusFilter }],
    queryFn: () => client.get('/platform-billing/invoices', { params: { page, limit: 20, status: statusFilter === 'ALL' ? undefined : statusFilter } }).then((r) => r.data),
  });

  const items = q.data?.data || [];
  const meta = q.data?.meta?.pagination || { page: 1, totalPages: 1 };

  async function saveCost() {
    try {
      await client.put('/platform-billing/cost-per-customer', { costPerCustomer: parseFloat(costValue) });
      toast.success('Cost updated');
      setCostEdit(false);
      qc.invalidateQueries({ queryKey: ['cost-per-customer'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }

  async function generate() {
    try {
      const res = await client.post('/platform-billing/invoices/generate', { month, year });
      const count = Array.isArray(res.data.data) ? res.data.data.length : 1;
      toast.success(`Generated ${count} invoice(s) for ${MONTHS[month-1]} ${year}`);
      setGenOpen(false);
      qc.invalidateQueries({ queryKey: ['platform-invoices'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }

  async function markPaid(inv) {
    try {
      await client.post(`/platform-billing/invoices/${inv.id}/mark-paid`);
      toast.success(`Invoice ${inv.invoiceNumber} marked paid`);
      qc.invalidateQueries({ queryKey: ['platform-invoices'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }

  return (
    <div data-testid="platform-billing-page">
      <PageHeader
        title="Platform billing"
        description="Global cost-per-customer setting & monthly platform invoices billed to each warehouse."
        actions={
          <Button onClick={() => setGenOpen(true)} className="bg-zinc-950 hover:bg-zinc-800 text-white h-10" data-testid="billing-generate-button">
            <PlayCircle className="w-4 h-4 mr-1.5" /> Generate invoices
          </Button>
        }
      />

      {/* Cost setting card */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6 flex items-center justify-between" data-testid="cost-per-customer-card">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Global cost per customer</div>
          {costQ.isLoading ? (
            <div className="h-9 w-40 rounded shimmer" />
          ) : costEdit ? (
            <div className="flex items-center gap-2">
              <Input
                type="number" step="0.01" min="0"
                value={costValue}
                onChange={(e) => setCostValue(e.target.value)}
                className="w-40 h-10 font-mono"
                data-testid="cost-input"
              />
              <Button size="sm" onClick={saveCost} className="bg-zinc-950 hover:bg-zinc-800 text-white h-10" data-testid="cost-save-button">Save</Button>
              <Button size="sm" variant="outline" onClick={() => setCostEdit(false)} className="h-10">Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="font-display text-3xl font-bold tabular-nums text-zinc-950" data-testid="cost-value">{formatCurrency(costQ.data?.costPerCustomer)}</div>
              <Button size="sm" variant="outline" onClick={() => { setCostValue(String(costQ.data?.costPerCustomer || 0)); setCostEdit(true); }} data-testid="cost-edit-button">Edit</Button>
            </div>
          )}
          <div className="text-xs text-zinc-500 mt-2 max-w-xl">Applied uniformly to all warehouses when generating monthly platform invoices. Bill = cost × customer count.</div>
        </div>
        <div className="w-14 h-14 rounded-lg bg-zinc-100 grid place-items-center">
          <DollarSign className="w-6 h-6 text-zinc-700" strokeWidth={1.5} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 h-10 border-zinc-200" data-testid="billing-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="UNPAID">UNPAID</SelectItem>
            <SelectItem value="PAID">PAID</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {q.isLoading ? <TableSkeleton /> : items.length === 0 ? (
          <EmptyState icon={DollarSign} title="No platform invoices yet" description="Click 'Generate invoices' to bill your warehouses for a specific month." />
        ) : (
          <table className="w-full text-sm" data-testid="platform-invoices-table">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-3">Invoice #</th>
                <th className="text-left px-4 py-3">Warehouse</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-right px-4 py-3">Customers</th>
                <th className="text-right px-4 py-3">Rate</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Paid on</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-zinc-50 transition-colors" data-testid={`pi-row-${i.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{i.invoiceNumber}</td>
                  <td className="px-4 py-3">{i.warehouse?.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{MONTHS[i.billingMonth-1]} {i.billingYear}</td>
                  <td className="px-4 py-3 text-right font-mono">{i.customerCount}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatCurrency(i.costPerCustomer)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{formatCurrency(i.amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{i.paidAt ? formatDate(i.paidAt) : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {i.status === 'UNPAID' && (
                      <Button size="sm" variant="outline" onClick={() => markPaid(i)} className="h-8" data-testid={`pi-mark-paid-${i.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Mark paid
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination page={meta.page} totalPages={meta.totalPages} onChange={setPage} />
      </div>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="generate-invoice-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Generate platform invoices</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-zinc-600">Select the billing month & year. One invoice will be created per active warehouse (idempotent).</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Month</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                  <SelectTrigger data-testid="gen-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Year</Label>
                <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="font-mono" data-testid="gen-year" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={generate} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="gen-submit">Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
